import { useState } from 'react'
import type { ComponentProps, JSX, PropsWithChildren } from 'react'

import {
  ChannelIcon,
  Flex,
  PdfIcon,
  PlayFilledIcon,
  Text,
} from '@buffer-mono/popcorn'

import { MediaMarker } from '~publish/components/MediaMarker/MediaMarker'
import { PostTypeBadge } from '~publish/components/PostType'
import { isChannelType } from '~publish/helpers/channels'
import { getProxiedThumbnailUrl } from '~publish/legacy/utils/bufferImages'

import { isMediaOnlyPost, MEDIA_ONLY_CAPTION } from '../utils'
import { MetricNumber } from './MetricNumber'

import styles from '../AllChannelsInsights/sections/AllChannelsTopPosts.module.css'

function TopPostCardRoot({
  label,
  children,
}: PropsWithChildren<{ label: string }>): JSX.Element {
  return (
    <article className={styles.card} aria-label={label}>
      {children}
    </article>
  )
}

function TopPostCardHeader({
  rank,
  metricValue,
  metricType,
}: {
  rank: number
  metricValue: number | null
  metricType: 'reactions' | 'comments'
}): JSX.Element {
  const noun = metricType === 'reactions' ? 'Reaction' : 'Comment'
  const label = metricValue === 1 ? noun : `${noun}s`
  return (
    <div className={styles.cardHeader}>
      <Text size="sm" className={styles.rank}>
        #{rank}
      </Text>
      <Text className={styles.metricValue}>
        {metricValue === null ? '—' : <MetricNumber value={metricValue} />}{' '}
        <Text as="span" weight="medium" color="subtle" size="sm">
          {label}
        </Text>
      </Text>
    </div>
  )
}

function TopPostCardPreview({ children }: PropsWithChildren): JSX.Element {
  return <div className={styles.postPreview}>{children}</div>
}

function TopPostCardMetadata({
  service,
  date,
  postType,
}: {
  service: string
  date: string | null
  postType: ComponentProps<typeof PostTypeBadge>['type']
}): JSX.Element {
  return (
    <div className={styles.previewHeader}>
      <Flex align="center" gap="2xs" className={styles.previewHeaderMeta}>
        {isChannelType(service) && (
          <ChannelIcon
            type={service}
            color="branded"
            size={14}
            className={styles.channelIcon}
          />
        )}
        {date && (
          <Text size="sm" color="subtle">
            {date}
          </Text>
        )}
      </Flex>
      <Flex align="center" gap="2xs" className={styles.previewHeaderEnd}>
        <PostTypeBadge type={postType} size="small" />
      </Flex>
    </div>
  )
}

function TopPostCardContent({
  text,
  assets,
}: {
  text?: string | null
  assets?: ReadonlyArray<{ type: string; thumbnail?: string | null }> | null
}): JSX.Element {
  /**
   * A thumbnail the browser can't fetch is dropped rather than drawn as a
   * placeholder: a broken frame reads as a Buffer bug, an absent one reads as
   * content we don't have. Tracked by url, so the card picks the thumbnail up
   * again once the media backfill re-hosts it under a new one.
   */
  const [unreachableThumbnail, setUnreachableThumbnail] = useState<
    string | null
  >(null)

  const asset = assets?.find((item) => item.thumbnail)
  const thumbnail = asset?.thumbnail
  const icon =
    asset?.type === 'video' ? (
      <PlayFilledIcon size="xsmall" />
    ) : asset?.type === 'document' ? (
      <PdfIcon size="xsmall" />
    ) : null
  return (
    <>
      <Text
        size="sm"
        className={styles.postText}
        color={text?.trim() ? undefined : 'subtle'}
      >
        {text?.trim() ? (
          <span translate="no">{text}</span>
        ) : isMediaOnlyPost({ text, assets }) ? (
          MEDIA_ONLY_CAPTION
        ) : (
          ''
        )}
      </Text>
      {thumbnail && thumbnail !== unreachableThumbnail && (
        <div className={styles.thumbnailSlot}>
          <div className={styles.thumbnailWrapper}>
            <img
              src={getProxiedThumbnailUrl(thumbnail)}
              alt=""
              className={styles.thumbnail}
              onError={(): void => setUnreachableThumbnail(thumbnail)}
            />
            {icon && (
              <MediaMarker className={styles.thumbnailBadge} aria-hidden>
                {icon}
              </MediaMarker>
            )}
          </div>
        </div>
      )}
    </>
  )
}

export const TopPostCard = Object.assign(TopPostCardRoot, {
  Header: TopPostCardHeader,
  Preview: TopPostCardPreview,
  Metadata: TopPostCardMetadata,
  Content: TopPostCardContent,
})
